import { ContextBarAppearanceProvider } from "@/contexts/ContextBarAppearanceContext";
import Email from "@/domain/entities/emails/Email";
import Thread from "@/domain/entities/emails/Thread";
import {
    cn,
    getDocumentsFromMailsList,
    getLinksFromMailsList,
    getTodosFromMailsList,
} from "@/lib/utils";
import { emailAttachmentToDisplay } from "@/mappers/email/displayAttachmentMapper";
import { useMemo } from "react";
import { ScrollArea } from "../ui/scroll-area";
import DocumentsLinksAndSimilar from "../widgets/attachements/DocumentsLinksAndSimilar";
import SmartTodos from "../widgets/todo/SmartTodos";
import MailSummary from "./MailSummary";

export enum ContextBarVariant {
    SQUEEZE,
    DEEP_FOCUS,
}

type ContextBarProps = {
    mail?: Email;
    mailsThread?: Thread;
    variant?: ContextBarVariant;
};

/**
 * What the side of a mail shows, read from its thread: the loop's summary, the to-dos, the documents
 * and the links. Shared by the context bar and the assistant's side panel, which lays them out in
 * one column.
 */
export const useMailContext = (mail?: Email, mailsThread?: Thread) => {
    const mailWithLatestAiDetails = useMemo(
        () =>
            mailsThread?.mails.find(
                (threadMail) =>
                    threadMail.internetMessageId === mail?.internetMessageId,
            ) ?? mail,
        [mail, mailsThread],
    );

    const todos = useMemo(
        () =>
            getTodosFromMailsList(
                mailWithLatestAiDetails
                    ? [mailWithLatestAiDetails]
                    : (mailsThread?.mails ?? []),
            ),
        [mailWithLatestAiDetails, mailsThread],
    );
    const documents = useMemo(
        () =>
            getDocumentsFromMailsList(mailsThread?.mails ?? []).map(
                emailAttachmentToDisplay,
            ),
        [mailsThread],
    );
    const links = useMemo(
        () => getLinksFromMailsList(mailsThread?.mails ?? []),
        [mailsThread],
    );

    return {
        summary: mailWithLatestAiDetails?.aiDetails?.longMailSummary,
        todos,
        documents,
        links,
    };
};

const ContextBar = ({ mail, mailsThread, variant }: ContextBarProps) => {
    const isDeepFocus = variant === ContextBarVariant.DEEP_FOCUS;
    const { summary, todos, documents, links } = useMailContext(
        mail,
        mailsThread,
    );

    return (
        <ContextBarAppearanceProvider isDeepFocus={isDeepFocus}>
            <ScrollArea className="min-w-0 shrink" variant="ghost">
                <div
                    className={cn(
                        "flex h-full min-w-0 max-w-full shrink flex-col gap-2 pr-2",
                        isDeepFocus ? "w-90 xl:w-110" : "w-40 xl:w-85",
                    )}
                >
                    {summary && <MailSummary content={summary} />}
                    <div className="flex h-full w-full flex-col gap-2 overflow-hidden">
                        <SmartTodos todos={todos} />

                        <DocumentsLinksAndSimilar
                            documents={documents}
                            links={links}
                            mailsList={mailsThread}
                        />
                    </div>
                </div>
            </ScrollArea>
        </ContextBarAppearanceProvider>
    );
};

export default ContextBar;
